// src/services/products.service.js - UPDATED VERSION with Array Support
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  serverTimestamp
} from "firebase/firestore";
import { db } from "./firebase/firebaseconfig";

class ProductsService {
  constructor() {
    this.collectionName = "products";
  }

  /**
   * Calculate price range for a product with variants
   */
  calculatePriceRange(product) {
    if (!product.hasVariants || !product.variants || product.variants.length === 0) {
      return {
        minPrice: product.price,
        maxPrice: product.oldPrice || product.price
      };
    }

    const prices = product.variants.map(v => v.price);
    return {
      minPrice: Math.min(...prices),
      maxPrice: Math.max(...prices)
    };
  }

  /**
   * Get display price for product card
   */
  getDisplayPrice(product) {
    if (!product.hasVariants || !product.variants || product.variants.length === 0) {
      return {
        price: product.price,
        oldPrice: product.oldPrice,
        priceText: product.price.toLocaleString() + ' DA'
      };
    }

    const priceRange = this.calculatePriceRange(product);
    
    if (priceRange.minPrice === priceRange.maxPrice) {
      return {
        price: priceRange.minPrice,
        oldPrice: null,
        priceText: priceRange.minPrice.toLocaleString() + ' DA'
      };
    }

    return {
      price: priceRange.minPrice,
      oldPrice: null,
      priceText: `À partir de ${priceRange.minPrice.toLocaleString()} DA`
    };
  }

  /**
   * Track deleted image publicIds
   */
  async deleteCloudinaryImages(publicIds) {
    const ids = Array.isArray(publicIds) ? publicIds : [publicIds];
    
    console.log('🗑️ Product deleted. Images to cleanup manually:', ids);
    console.log('Go to Cloudinary Media Library and delete these publicIds if needed');
    
    try {
      const existingDeleted = JSON.parse(localStorage.getItem('cloudinary_deleted_images') || '[]');
      const updated = [...existingDeleted, ...ids.map(id => ({
        publicId: id,
        deletedAt: new Date().toISOString()
      }))];
      localStorage.setItem('cloudinary_deleted_images', JSON.stringify(updated));
    } catch (e) {
      console.log('Could not store deleted images list');
    }
    
    return { 
      success: true, 
      message: 'Product deleted. Images tracked for manual cleanup',
      deletedIds: ids
    };
  }

  async getAllProducts() {
    try {
      const productsRef = collection(db, this.collectionName);
      const querySnapshot = await getDocs(productsRef);
      
      const products = [];
      querySnapshot.forEach((doc) => {
        const productData = {
          id: doc.id,
          ...doc.data()
        };
        
        // Add computed fields
        const priceInfo = this.getDisplayPrice(productData);
        productData.displayPrice = priceInfo.price;
        productData.displayPriceText = priceInfo.priceText;
        
        products.push(productData);
      });
      
      return {
        success: true,
        products: products
      };
    } catch (error) {
      console.error("Get all products error:", error);
      return {
        success: false,
        error: error.message,
        products: []
      };
    }
  }

  async getProductById(productId) {
    try {
      const productRef = doc(db, this.collectionName, productId);
      const productSnap = await getDoc(productRef);

      if (!productSnap.exists()) {
        return {
          success: false,
          error: "Product not found",
          product: null
        };
      }

      const productData = {
        id: productSnap.id,
        ...productSnap.data()
      };

      return {
        success: true,
        product: productData
      };
    } catch (error) {
      console.error("Get product by ID error:", error);
      return {
        success: false,
        error: error.message,
        product: null
      };
    }
  }

  async getProductsByCategory(category) {
    try {
      const productsRef = collection(db, this.collectionName);
      const q = query(productsRef, where("category", "==", category));
      const querySnapshot = await getDocs(q);

      const products = [];
      querySnapshot.forEach((doc) => {
        const productData = {
          id: doc.id,
          ...doc.data()
        };
        
        const priceInfo = this.getDisplayPrice(productData);
        productData.displayPrice = priceInfo.price;
        productData.displayPriceText = priceInfo.priceText;
        
        products.push(productData);
      });

      return {
        success: true,
        products: products
      };
    } catch (error) {
      console.error("Get products by category error:", error);
      return {
        success: false,
        error: error.message,
        products: []
      };
    }
  }

  async searchProducts(searchTerm) {
    try {
      const productsRef = collection(db, this.collectionName);
      const querySnapshot = await getDocs(productsRef);

      const products = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const searchLower = searchTerm.toLowerCase();
        
        if (
          data.name?.toLowerCase().includes(searchLower) ||
          data.description?.toLowerCase().includes(searchLower) ||
          data.category?.toLowerCase().includes(searchLower) ||
          data.brand?.toLowerCase().includes(searchLower)
        ) {
          const productData = {
            id: doc.id,
            ...data
          };
          
          const priceInfo = this.getDisplayPrice(productData);
          productData.displayPrice = priceInfo.price;
          productData.displayPriceText = priceInfo.priceText;
          
          products.push(productData);
        }
      });

      return {
        success: true,
        products: products
      };
    } catch (error) {
      console.error("Search products error:", error);
      return {
        success: false,
        error: error.message,
        products: []
      };
    }
  }

  async addProduct(productData) {
    try {
      const productsRef = collection(db, this.collectionName);
      const docRef = await addDoc(productsRef, {
        ...productData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        productId: docRef.id,
        message: "Product added successfully"
      };
    } catch (error) {
      console.error("Add product error:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to add product"
      };
    }
  }

  async updateProduct(productId, updates) {
    try {
      const productRef = doc(db, this.collectionName, productId);
      await updateDoc(productRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        message: "Product updated successfully"
      };
    } catch (error) {
      console.error("Update product error:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to update product"
      };
    }
  }

  async deleteProduct(productId) {
    try {
      const productResult = await this.getProductById(productId);
      
      if (productResult.success && productResult.product) {
        const product = productResult.product;
        const publicIdsToTrack = [];
        
        if (product.imagePublicId) {
          publicIdsToTrack.push(product.imagePublicId);
        }
        
        if (product.image && typeof product.image === 'object' && product.image.publicId) {
          publicIdsToTrack.push(product.image.publicId);
        }
        
        if (product.images && Array.isArray(product.images)) {
          product.images.forEach(img => {
            if (typeof img === 'object' && img.publicId) {
              publicIdsToTrack.push(img.publicId);
            }
          });
        }
        
        if (publicIdsToTrack.length > 0) {
          await this.deleteCloudinaryImages(publicIdsToTrack);
        }
      }
      
      const productRef = doc(db, this.collectionName, productId);
      await deleteDoc(productRef);

      return {
        success: true,
        message: "Product deleted successfully"
      };
    } catch (error) {
      console.error("Delete product error:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to delete product"
      };
    }
  }

  async getProductsPaginated(pageSize = 12, lastDoc = null) {
    try {
      const productsRef = collection(db, this.collectionName);
      let q;

      if (lastDoc) {
        q = query(
          productsRef,
          orderBy("createdAt", "desc"),
          startAfter(lastDoc),
          limit(pageSize)
        );
      } else {
        q = query(
          productsRef,
          orderBy("createdAt", "desc"),
          limit(pageSize)
        );
      }

      const querySnapshot = await getDocs(q);
      const products = [];
      let lastVisible = null;

      querySnapshot.forEach((doc) => {
        const productData = {
          id: doc.id,
          ...doc.data()
        };
        
        const priceInfo = this.getDisplayPrice(productData);
        productData.displayPrice = priceInfo.price;
        productData.displayPriceText = priceInfo.priceText;
        
        products.push(productData);
        lastVisible = doc;
      });

      return {
        success: true,
        products: products,
        lastDoc: lastVisible,
        hasMore: products.length === pageSize
      };
    } catch (error) {
      console.error("Get products paginated error:", error);
      return {
        success: false,
        error: error.message,
        products: [],
        lastDoc: null,
        hasMore: false
      };
    }
  }

  async getFeaturedProducts(limitCount = 8) {
    try {
      const productsRef = collection(db, this.collectionName);
      const q = query(
        productsRef,
        where("badge", "in", ["Bestseller", "Hot", "Nouveau"]),
        limit(limitCount)
      );
      const querySnapshot = await getDocs(q);

      const products = [];
      querySnapshot.forEach((doc) => {
        const productData = {
          id: doc.id,
          ...doc.data()
        };
        
        const priceInfo = this.getDisplayPrice(productData);
        productData.displayPrice = priceInfo.price;
        productData.displayPriceText = priceInfo.priceText;
        
        products.push(productData);
      });

      return {
        success: true,
        products: products
      };
    } catch (error) {
      console.error("Get featured products error:", error);
      return {
        success: false,
        error: error.message,
        products: []
      };
    }
  }

  // UPDATED: Filter logic with array support for colors and materials
  async getProductsByFilter(filters) {
    try {
      const productsRef = collection(db, this.collectionName);
      let q = query(productsRef);

      if (filters.category && filters.category.length > 0) {
        q = query(q, where("category", "in", filters.category));
      }

      const querySnapshot = await getDocs(q);
      let products = [];

      querySnapshot.forEach((doc) => {
        const productData = {
          id: doc.id,
          ...doc.data()
        };
        
        const priceInfo = this.getDisplayPrice(productData);
        productData.displayPrice = priceInfo.price;
        productData.displayPriceText = priceInfo.priceText;
        
        products.push(productData);
      });

      // Client-side filtering for price
      if (filters.priceRange) {
        products = products.filter(p => {
          const price = p.displayPrice || p.price;
          return price >= filters.priceRange.min && price <= filters.priceRange.max;
        });
      }

      // UPDATED: Materials filter with array support
      if (filters.materials && filters.materials.length > 0) {
        products = products.filter(p => {
          if (!p.hasVariants) {
            // For non-variant products, check single material
            return filters.materials.includes(p.material);
          }
          // For variant products, check if ANY variant has ANY of the selected materials
          return p.variants?.some(v => {
            if (Array.isArray(v.materials)) {
              // Check if variant's materials array contains any of the filter materials
              return v.materials.some(mat => filters.materials.includes(mat));
            } else if (v.material) {
              // Backward compatibility: single material
              return filters.materials.includes(v.material);
            }
            return false;
          });
        });
      }

      // UPDATED: Colors filter with array support
      if (filters.colors && filters.colors.length > 0) {
        products = products.filter(p => {
          if (!p.hasVariants) {
            // For non-variant products, check single color
            return filters.colors.includes(p.color);
          }
          // For variant products, check if ANY variant has ANY of the selected colors
          return p.variants?.some(v => {
            if (Array.isArray(v.colors)) {
              // Check if variant's colors array contains any of the filter colors
              return v.colors.some(col => filters.colors.includes(col));
            } else if (v.color) {
              // Backward compatibility: single color
              return filters.colors.includes(v.color);
            }
            return false;
          });
        });
      }

      return {
        success: true,
        products: products
      };
    } catch (error) {
      console.error("Get products by filter error:", error);
      return {
        success: false,
        error: error.message,
        products: []
      };
    }
  }
}

export default new ProductsService();